'use client';

import { useMemo, type ReactEventHandler, type ReactNode, type Ref } from 'react';
import { Match, Option, Predicate, String as Str } from 'effect';

import {
  useVsImage,
  type ImageBypassRule,
  type VsImageProps,
  type VsImageSrc,
  type VsStaticImageData,
} from '@vs-shop/headless/useVsImage';
import { ImageMissingPlaceholder } from '@vs-shop/ui/imageMissingPlaceholder';
import { clsxm } from '@vs-shop/utils';

/**
 * A statically imported image in the shape a bundler without ESM interop hands
 * over (`{ default: StaticImageData }`). `next/image` accepts it as
 * `StaticRequire`.
 */
type VsStaticRequire = { readonly default: VsStaticImageData };

/**
 * What the smart image accepts: the portable `VsImageProps` contract, with a
 * source that may also be a static import, plus the optimizer `quality`.
 * Pinned by `makeVsImage.tst.ts`.
 */
export type SmartImageProps = Omit<VsImageProps, 'src'> & {
  src: VsImageSrc | VsStaticRequire;
  quality?: number;
};

/**
 * What the smart image hands to the host's image component. Every prop is one
 * `next/image` accepts with a compatible type, so `next/image` satisfies
 * {@link VsImageHost} without a cast.
 */
export type VsImageHostProps = Omit<
  VsImageProps,
  'src' | 'preload' | 'fallbackSrc' | 'fallback' | 'unoptimized' | 'onError'
> & {
  src: VsImageSrc;
  quality?: number;
  ref: Ref<HTMLImageElement>;
  unoptimized: boolean;
  onError: ReactEventHandler<HTMLImageElement>;
};

/** The host's image component — `next/image` in a Next shell. */
export type VsImageHost = (props: VsImageHostProps) => ReactNode;

export type MakeVsImageOptions = {
  readonly Image: VsImageHost;
  /** Sources that skip the host's optimizer; see `buildImageBypassRules`. */
  readonly bypassRules: ImageBypassRule[];
  /**
   * The URL to retry a failed URL source with, `None` when there is none. A
   * statically imported source never reaches it.
   */
  readonly resolveFallback: (src: string) => Option.Option<string>;
};

function toVsImageSrc(src: SmartImageProps['src']): VsImageSrc {
  if (Predicate.isString(src)) return src;
  return Predicate.hasProperty(src, 'default') ? src.default : src;
}

/**
 * Builds the smart image a delivery shell aliases `@vs-shop/image-link/Image`
 * to. The shell injects its image component, its bypass rules and its fallback
 * resolver; the behaviour is the same for every storefront:
 *
 * - the source renders through the host's component, which is skipped by the
 *   optimizer when a bypass rule matches and otherwise asks for quality 95;
 * - a failure retries once with `fallbackSrc`, or with the resolved fallback
 *   of a URL source;
 * - a second failure, or a first one with no fallback, paints the
 *   missing-image placeholder in a box that keeps the image's `className`.
 */
export function makeVsImage({ Image, bypassRules, resolveFallback }: MakeVsImageOptions) {
  return function VsImage({
    src: source,
    unoptimized: requestedUnoptimized,
    preload,
    onError: userOnError,
    fallbackSrc,
    fallback: terminalFallback,
    ...props
  }: SmartImageProps) {
    const src = toVsImageSrc(source);

    const fallback = useMemo<Option.Option<VsImageSrc>>(
      () =>
        Option.orElse(Option.filter(Option.fromNullishOr(fallbackSrc), Str.isNonEmpty), () =>
          Option.flatMap(Option.filter(Option.some(src), Predicate.isString), resolveFallback)
        ),
      [fallbackSrc, src]
    );

    const { stage, current, imgRef, isError, handleError, unoptimized } = useVsImage<VsImageSrc>({
      src,
      fallback,
      preload: preload ?? false,
      unoptimized: requestedUnoptimized ?? false,
      bypass: bypassRules,
    });

    if (isError) {
      return (
        <span className={clsxm('flex items-center justify-center', props.className)}>
          {Option.getOrElse(Option.fromNullishOr(terminalFallback), () => (
            <ImageMissingPlaceholder />
          ))}
        </span>
      );
    }

    const handleImageError: ReactEventHandler<HTMLImageElement> = (event) => {
      handleError(event.currentTarget);
      userOnError?.(event);
    };

    // Unoptimized images without dimensions keep the browser's intrinsic geometry.
    const intrinsicSource = Option.filter(Option.some(current), Predicate.isString);
    const useIntrinsicHost =
      requestedUnoptimized === true &&
      props.fill !== true &&
      (Predicate.isUndefined(props.width) || Predicate.isUndefined(props.height)) &&
      Option.isSome(intrinsicSource);

    const { quality: _quality, fill: _fill, ...intrinsicProps } = props;

    return Match.value(useIntrinsicHost).pipe(
      Match.when(true, () => (
        <img
          key={stage}
          {...intrinsicProps}
          ref={imgRef}
          src={Option.getOrThrow(intrinsicSource)}
          alt={props.alt}
          onError={handleImageError}
        />
      )),
      Match.when(false, () => (
        <Image
          // Force a fresh <img> DOM node on stage flip. Without this, React
          // mutates the existing element's `src`, but Firefox leaves the prior
          // failure's `complete=true / naturalWidth=0` painted on it — both as
          // browser visual residue and as readable DOM properties that confuse
          // any downstream check. A new key remounts cleanly.
          key={stage}
          {...(!unoptimized && { quality: 95 })}
          {...props}
          ref={imgRef}
          src={current}
          unoptimized={unoptimized}
          onError={handleImageError}
        />
      )),
      Match.exhaustive
    );
  };
}
